import React, { useState } from 'react';
import { Form, Input, Select, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { StepComponentProps } from './OrganizationWizard';
import { TimeZones } from 'common/constants/TimeZones';
import { organizationApi } from '../api/organizationApi';
import { useToasts } from 'common/toast';

const TIER_OPTIONS = [
  { key: 1, value: 1, text: 'Tier 1 - Insight' },
  { key: 2, value: 2, text: 'Tier 2 - Improve' },
  { key: 3, value: 3, text: 'Tier 3 - Lead' },
  { key: 4, value: 4, text: 'Tier 4 - Enterprise' },
];

const OrganizationWizardStep1Properties: React.FC<StepComponentProps> = ({
  data,
  onDataChange,
  onNext,
  onBack,
  onCancel,
  isFirstStep,
}) => {
  const { addToast } = useToasts();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);

  const [createOrganization] = organizationApi.useApiAdminV1OrganizationCreateMutation();
  const [updateOrganization] = organizationApi.useApiAdminV1OrganizationPartialUpdateMutation();

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!data.name?.trim()) {
      newErrors.name = 'Organization name is required';
    }
    if (!data.time_zone) {
      newErrors.time_zone = 'Time zone is required';
    }
    if (!data.tier) {
      newErrors.tier = 'Tier is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validate()) return;

    setIsCreating(true);
    try {
      if (data.organizationId) {
        // Organization already exists, just update it
        await updateOrganization({
          id: data.organizationId,
          patchedOrganization: {
            name: data.name,
            time_zone: data.time_zone,
            tier: data.tier,
          } as any,
        }).unwrap();
        addToast('Organization updated', { appearance: 'success' });
      } else {
        // Create new organization
        const result = await createOrganization({
          organization: {
            name: data.name,
            time_zone: data.time_zone,
            tier: data.tier,
          } as any,
        }).unwrap();

        // Store the organization ID for subsequent steps
        onDataChange({ organizationId: result.id });
        addToast('Organization created successfully', { appearance: 'success' });
      }

      onNext();
    } catch (error) {
      console.error('Failed to save organization:', error);
      addToast('Failed to save organization', { appearance: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <StepContainer>
      <StepHeader>
        <StepTitle>Organization Properties</StepTitle>
        <StepSubtitle>
          Enter the basic information for your organization. This information can be changed later.
        </StepSubtitle>
      </StepHeader>

      <SectionCard>
        <Form>
          <Form.Field required error={!!errors.name}>
            <label>Organization Name</label>
            <Input
              placeholder="Enter organization name"
              value={data.name}
              onChange={(_, { value }) => {
                onDataChange({ name: value });
                if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
              }}
            />
            {errors.name && <ErrorText>{errors.name}</ErrorText>}
          </Form.Field>

          <Form.Group widths="equal">
            <Form.Field required error={!!errors.time_zone}>
              <label>Time Zone</label>
              <Select
                placeholder="Select time zone"
                options={TimeZones.map(tz => ({ key: tz.value, value: tz.value, text: tz.text }))}
                value={data.time_zone}
                onChange={(_, { value }) => {
                  onDataChange({ time_zone: value as string });
                  if (errors.time_zone) setErrors(prev => ({ ...prev, time_zone: '' }));
                }}
                search
              />
              {errors.time_zone && <ErrorText>{errors.time_zone}</ErrorText>}
            </Form.Field>

            <Form.Field required error={!!errors.tier}>
              <label>Tier</label>
              <Select
                placeholder="Select tier"
                options={TIER_OPTIONS}
                value={data.tier}
                onChange={(_, { value }) => {
                  onDataChange({ tier: value as number });
                  if (errors.tier) setErrors(prev => ({ ...prev, tier: '' }));
                }}
              />
              {errors.tier && <ErrorText>{errors.tier}</ErrorText>}
            </Form.Field>
          </Form.Group>
        </Form>
      </SectionCard>

      <ButtonContainer>
        <LeftButtons>
          <BackButton onClick={onBack} disabled={isFirstStep}>
            Back
          </BackButton>
          <CancelButton onClick={onCancel}>
            Cancel Setup
          </CancelButton>
        </LeftButtons>
        <NextButton onClick={handleContinue} disabled={isCreating}>
          {isCreating ? 'Creating...' : data.organizationId ? 'Continue' : 'Create & Continue'}
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default OrganizationWizardStep1Properties;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-right: 32px;
`;

const StepHeader = styled.div`
  margin-bottom: 20px;
`;

const StepTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const StepSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const SectionCard = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
`;

const ErrorText = styled.div`
  color: #db2828;
  font-size: 12px;
  margin-top: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const BackButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }
`;

const NextButton = styled.button`
  padding: 10px 24px;
  background: #2185d0;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #1678c2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
